"use client";

import React from 'react';
import Link from 'next/link';

import { LayoutDashboard, Bot, ScrollText, Settings, ChevronLeft, ChevronRight, X,  } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import Icon from '@/components/ui/AppIcon';


interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  currentPath: string;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const navItems = [
  { key: 'nav_overview', path: '/overview-dashboard', icon: LayoutDashboard, badge: null },
  { key: 'nav_ai_assistant', path: '/ai-threat-assistant', icon: Bot, badge: '2' },
  { key: 'nav_audit_logs', path: '/security-audit-logs', icon: ScrollText, badge: '47' },
  { key: 'nav_settings', path: '/settings', icon: Settings, badge: null },
] as const;

export default function Sidebar({
  collapsed,
  onToggleCollapse,
  currentPath,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const { language } = useLanguage();

  const isActive = (path: string) => currentPath === path || currentPath.startsWith(path + '/');

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`
          hidden lg:flex flex-col
          bg-[#080B12] border-r border-cyber-border
          transition-all duration-300 ease-in-out
          relative z-30 shrink-0
          ${collapsed ? 'w-16' : 'w-60'}
        `}
        aria-label="Main navigation"
        role="navigation"
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 px-4 py-5 border-b border-cyber-border ${collapsed ? 'justify-center px-2' : ''}`}>
          <div className="relative shrink-0">
            <img
              src="/assets/images/Otek1_-SUBER_BEST_-1773730349362.jpeg"
              alt="Otek1-Syber cybersecurity platform logo"
              width={36}
              height={36}
              className="rounded-lg object-cover ring-1 ring-cyber-blue/30"
              style={{ width: 36, height: 36 }}
            />
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-cyber-green rounded-full border border-[#080B12] live-dot" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <p className={`text-sm font-bold text-white leading-tight tracking-wide ${language === 'bn' ? 'font-bangla' : ''}`}>
                {t(language, 'nav_platform')}
              </p>
              <p className={`text-[10px] text-cyber-blue leading-tight truncate ${language === 'bn' ? 'font-bangla' : ''}`}>
                {t(language, 'nav_tagline')}
              </p>
            </div>
          )}
        </div>

        {/* System Status */}
        {!collapsed && (
          <div className="mx-3 my-3 px-3 py-2 bg-cyber-green/5 border border-cyber-green/20 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-cyber-green rounded-full live-dot" />
              <span className="text-[10px] font-mono text-cyber-green font-medium">SYSTEMS OPERATIONAL</span>
            </div>
            <p className="text-[10px] text-cyber-muted mt-0.5 font-mono">Threat Engine v3.2.1 Active</p>
          </div>
        )}

        {/* Nav Items */}
        <nav className="flex-1 px-2 py-2 space-y-1" aria-label="Primary navigation">
          {navItems.map((item) => {
            const active = isActive(item.path);
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                href={item.path}
                title={collapsed ? t(language, item.key) : undefined}
                aria-label={`${t(language, item.key)}${item.badge ? `, ${item.badge} alerts` : ''}`}
                aria-current={active ? 'page' : undefined}
                className={`
                  group flex items-center gap-3 px-3 py-2.5 rounded-lg
                  transition-all duration-150 relative
                  ${active
                    ? 'bg-cyber-blue/10 text-cyber-blue border border-cyber-blue/20' :'text-cyber-muted hover:bg-white/5 hover:text-cyber-text border border-transparent'
                  }
                  ${collapsed ? 'justify-center px-2' : ''}
                `}
              >
                {active && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-cyber-blue rounded-full" />
                )}
                <Icon
                  size={18}
                  className={`shrink-0 ${active ? 'text-cyber-blue' : 'text-cyber-muted group-hover:text-cyber-text'}`}
                />
                {!collapsed && (
                  <>
                    <span className={`flex-1 text-sm font-medium ${language === 'bn' ? 'font-bangla text-[13px]' : ''}`}>
                      {t(language, item.key)}
                    </span>
                    {item.badge && (
                      <span className="text-[10px] bg-cyber-red/20 text-cyber-red border border-cyber-red/30 rounded-full px-1.5 py-0.5 font-mono font-medium">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
                {collapsed && item.badge && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-cyber-red rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Threat Summary */}
        {!collapsed && (
          <div className="mx-3 mb-3 px-3 py-3 bg-[#0F1117] border border-cyber-border rounded-lg">
            <p className="text-[10px] text-cyber-muted uppercase tracking-widest mb-2 font-mono">Threat Summary</p>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-cyber-red rounded-full" />
                  <span className="text-[11px] text-cyber-muted">Critical</span>
                </div>
                <span className="text-[11px] font-mono text-cyber-red font-medium">3</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
                  <span className="text-[11px] text-cyber-muted">High</span>
                </div>
                <span className="text-[11px] font-mono text-orange-400 font-medium">11</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full" />
                  <span className="text-[11px] text-cyber-muted">Medium</span>
                </div>
                <span className="text-[11px] font-mono text-yellow-400 font-medium">28</span>
              </div>
            </div>
          </div>
        )}

        {/* User Profile */}
        <div className={`border-t border-cyber-border p-3 flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-cyber-blue/20 border border-cyber-blue/30 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-cyber-blue">OK</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-cyber-text truncate">Opel Khan</p>
              <p className="text-[10px] text-cyber-muted truncate">CISO · BTRC</p>
            </div>
          )}
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className="absolute -right-3 top-20 w-6 h-6 bg-cyber-surface border border-cyber-border rounded-full flex items-center justify-center hover:bg-cyber-blue/10 hover:border-cyber-blue/30 transition-all duration-150 z-50"
        >
          {collapsed
            ? <ChevronRight size={12} className="text-cyber-muted" />
            : <ChevronLeft size={12} className="text-cyber-muted" />
          }
        </button>
      </aside>

      {/* Mobile Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-72
          bg-[#080B12] border-r border-cyber-border
          flex flex-col lg:hidden
          transition-transform duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        aria-label="Mobile navigation"
        role="navigation"
        aria-hidden={!mobileOpen}
      >
        <div className="flex items-center justify-between px-4 py-5 border-b border-cyber-border">
          <div className="flex items-center gap-3">
            <img
              src="/assets/images/Otek1_-SUBER_BEST_-1773730349362.jpeg"
              alt="Otek1-Syber logo"
              width={36}
              height={36}
              className="rounded-lg object-cover ring-1 ring-cyber-blue/30"
              style={{ width: 36, height: 36 }}
            />
            <div>
              <p className="text-sm font-bold text-white">Otek1-Syber</p>
              <p className="text-[10px] text-cyber-blue">Cyber Intelligence Platform</p>
            </div>
          </div>
          <button
            onClick={onMobileClose}
            aria-label="Close navigation menu"
            className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <X size={16} className="text-cyber-muted" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1" aria-label="Mobile primary navigation">
          {navItems.map((item) => {
            const active = isActive(item.path);
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                href={item.path}
                onClick={onMobileClose}
                aria-label={`${t(language, item.key)}${item.badge ? `, ${item.badge} alerts` : ''}`}
                aria-current={active ? 'page' : undefined}
                className={`
                  flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-150
                  ${active
                    ? 'bg-cyber-blue/10 text-cyber-blue border border-cyber-blue/20' :'text-cyber-muted hover:bg-white/5 hover:text-cyber-text border border-transparent'
                  }
                `}
              >
                <Icon size={18} className={active ? 'text-cyber-blue' : 'text-cyber-muted'} />
                <span className="text-sm font-medium">{t(language, item.key)}</span>
                {item.badge && (
                  <span className="ml-auto text-[10px] bg-cyber-red/20 text-cyber-red border border-cyber-red/30 rounded-full px-1.5 py-0.5 font-mono">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
}